import {
  INPUT_TYPE_LABEL,
  ORIGINS,
  ORIGIN_LABEL,
  STAGE_LABEL,
  daysInLab,
  type JobOrder,
} from '@/domain';
import { formatDay, labToday } from '@/shared/format/instant';
import { formatAmount } from '@/shared/format/money';
import { strings } from '@/shared/strings';
import type { Column } from './ResponsiveTable';

const text = strings.table.columns;

/**
 * `45m`, `3h`, `169h 3m`: how long a case has been where it is, in the form the
 * production line and Final Check read it in (`formatDuration`, which is the
 * application's and so not this layer's to import).
 */
function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * What a job order carries that a list of them need not show as it comes. The
 * request of 8 October 2026 (second): "if there are data that are not in the
 * table, but we can show, add them".
 *
 * One definition of each, so Created reads the same on Orders as on Payments
 * and a screen only says which of them it offers. Every one is `optional`: off
 * until its reader ticks it in Customize columns. A header sorts the page in
 * hand, as every other header of these tables does.
 *
 * Nothing here is asked of the server. A value withheld from the caller reads
 * Hidden, as a withheld name does.
 */
const OFFERED = {
  createdAt: {
    /*
     * The day the case arrived at the lab, which is the moment it was booked
     * in. Named Received, the lab's word for it and the one Final Check and the
     * production line read it under; it read Created here. The lab's request
     * of 11 October 2026.
     */
    key: 'createdAt',
    label: text.received,
    mono: true,
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.createdAt ?? '',
    render: (order) => (order.createdAt === undefined ? '' : formatDay(order.createdAt)),
  },
  startDate: {
    key: 'startDate',
    label: text.startDate,
    mono: true,
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.startDate ?? '',
    render: (order) => order.startDate ?? '',
  },
  inLab: {
    // Whole days since it arrived. Nothing for a case that has left the lab.
    key: 'inLab',
    label: text.inLab,
    mono: true,
    align: 'right',
    nowrap: true,
    sortable: true,
    sortValue: (order) => (order.currentStage === null ? -1 : (daysInLab(order.createdAt, labToday()) ?? -1)),
    render: (order) => {
      const days = order.currentStage === null ? null : daysInLab(order.createdAt, labToday());
      return days === null ? '' : `${days}${strings.orders.daysShort}`;
    },
  },
  inStage: {
    // How long it has been where it is now, under the name the production line gives it.
    key: 'inStage',
    label: strings.stage.colInStage,
    mono: true,
    align: 'right',
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.minutesInStage ?? -1,
    render: (order) => (order.minutesInStage == null ? '' : formatMinutes(order.minutesInStage)),
  },
  caseType: {
    key: 'caseType',
    label: text.caseType,
    sortable: true,
    sortValue: (order) => (order.input == null ? '' : INPUT_TYPE_LABEL[order.input]),
    render: (order) => (order.input == null ? '' : INPUT_TYPE_LABEL[order.input]),
  },
  product: {
    key: 'product',
    label: text.item,
    sortable: true,
    sortValue: (order) => order.product ?? '',
    render: (order) => order.product ?? '',
  },
  teeth: {
    key: 'teeth',
    label: text.teeth,
    mono: true,
    render: (order) => (order.teeth ?? []).join(', '),
  },
  units: {
    key: 'units',
    label: text.units,
    mono: true,
    align: 'right',
    sortable: true,
    sortValue: (order) => order.units ?? 0,
    render: (order) => order.units ?? '',
  },
  shade: {
    key: 'shade',
    label: text.shade,
    sortable: true,
    sortValue: (order) => order.shade ?? '',
    render: (order) => order.shade ?? '',
  },
  noteCount: {
    key: 'noteCount',
    label: text.notes,
    mono: true,
    align: 'right',
    sortable: true,
    sortValue: (order) => order.noteCount ?? 0,
    render: (order) => (order.noteCount == null || order.noteCount === 0 ? '' : order.noteCount),
  },
  photoCount: {
    key: 'photoCount',
    label: text.photos,
    mono: true,
    align: 'right',
    sortable: true,
    sortValue: (order) => order.photoCount ?? 0,
    render: (order) => (order.photoCount == null || order.photoCount === 0 ? '' : order.photoCount),
  },
  origin: {
    key: 'origin',
    label: strings.table.origin,
    sortable: true,
    // The client's order, not the alphabet's.
    sortValue: (order) => ORIGINS.indexOf(order.origin),
    render: (order) => ORIGIN_LABEL[order.origin],
  },
  currentStage: {
    // Where the case is and who has it.
    key: 'currentStage',
    label: strings.orders.withWhom,
    sortable: true,
    sortValue: (order) => (order.currentStage === null ? '' : STAGE_LABEL[order.currentStage]),
    render: (order) =>
      order.currentStage === null
        ? strings.orders.dispatchedAway
        : `${STAGE_LABEL[order.currentStage]} · ${order.holderName ?? strings.orders.nobodyYet}`,
  },
  value: {
    key: 'value',
    label: text.value,
    mono: true,
    align: 'right',
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.value ?? 0,
    // In the system's money: the lab's request of 17 October 2026, item 13.
    render: (order) => (order.value == null ? strings.table.hidden : formatAmount(order.value)),
  },
  paidAt: {
    // The day it was paid, and nothing for a case that is not.
    key: 'paidAt',
    label: text.paid,
    mono: true,
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.payment.paidAt ?? '',
    render: (order) =>
      !order.payment.paid ? '' : order.payment.paidAt === null ? text.yes : formatDay(order.payment.paidAt),
  },
  clinic: {
    // Absent with the names, for a caller without seeIdentity.
    key: 'clinic',
    label: text.clinic,
    sortable: true,
    sortValue: (order) => order.deliverTo?.clinic ?? '',
    render: (order) =>
      order.deliverTo === undefined ? strings.table.hidden : (order.deliverTo?.clinic ?? ''),
  },
  // The number on the doctor's paper. The lab's request of 17 October 2026, item 7.
  serialNumber: {
    key: 'serialNumber',
    label: text.serialNumber,
    mono: true,
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.serialNumber ?? '',
    render: (order) => order.serialNumber ?? '',
  },
  redoOf: {
    key: 'redoOf',
    label: text.redoOf,
    mono: true,
    nowrap: true,
    sortable: true,
    sortValue: (order) => order.redoOf ?? '',
    render: (order) => order.redoOf ?? '',
  },
  carrier: {
    key: 'carrier',
    label: text.carrier,
    sortable: true,
    sortValue: (order) => order.dispatch.carrier ?? '',
    render: (order) => order.dispatch.carrier ?? '',
  },
  tracking: {
    key: 'tracking',
    label: text.tracking,
    mono: true,
    render: (order) => order.dispatch.trackingCode ?? '',
  },
} satisfies Record<string, Column<JobOrder>>;

export type OrderColumnKey = keyof typeof OFFERED;

/** The offered columns a screen names, in the order it names them. */
export function orderColumns(keys: readonly OrderColumnKey[]): Column<JobOrder>[] {
  return keys.map((key) => ({ ...(OFFERED[key] as Column<JobOrder>), optional: true }));
}

/** Whether a case's value is sent to somebody: only a holder of `pay` or `admin` receives it. */
export function seesCaseValue(permissions: readonly string[] | undefined): boolean {
  return permissions?.includes('pay') === true || permissions?.includes('admin') === true;
}
